/**
 * The bill of materials for one side of the set boundary: the single-use set,
 * or what is outside it. The Inspector lists it while one side is shown on its
 * own; the schematic's SVG and PDF can carry it as a table (D35).
 *
 * Each line is in the unit it is bought in: parts by the piece, split by
 * capacity where a part has one (a 2 L bag is not a 500 mL bag), and each kind
 * of tube by bore, wall and material, by the metre, with the number of cuts.
 */
import type { DesignDocument } from "../design/document";
import type { StillTable } from "../render/still";
import { THEME } from "../render/theme";
import { partition } from "../scene/consumable";
import type { WorkspaceLibrary } from "./library";
import { effectiveParameters } from "./parameters";
import { displayUnits, formatNumber, formatQuantity, fromCanonical, liquidUnitFor, type DisplayUnit, type LiquidUnits } from "./units";

export type BomSide = "inside" | "outside";

export interface BomLine {
  item: string;
  count: number;
  /** Total tube length, canonical metres; null when a cut has no length yet. */
  length: number | null;
  byLength: boolean;
  /** What on the drawing the line counts. */
  refs: string[];
}

export interface BillOfMaterials {
  side: BomSide;
  lines: BomLine[];
  /** The unit tube is listed in. */
  tubeUnit: DisplayUnit;
}

function displayUnitNamed(canonical: string, label: string): DisplayUnit {
  return displayUnits(canonical).find((unit) => unit.label === label)!;
}

const MILLIMETRE = displayUnitNamed("meter", "mm");
const METRE = displayUnitNamed("meter", "m");

export function billOfMaterials(
  document: DesignDocument,
  library: WorkspaceLibrary,
  side: BomSide,
  liquid: LiquidUnits | null,
): BillOfMaterials {
  const shown = partition(document, library.scene.templates)[side];
  // Tube is bought by the metre; a chosen length unit (D49) wins.
  const lengthUnit = liquid?.length ?? null;
  const tubeUnit = lengthUnit ?? METRE;
  const lines = new Map<string, BomLine>();
  const add = (key: string, ref: string, item: string, byLength: boolean, length?: number | null) => {
    const line = lines.get(key) ?? { item, count: 0, length: 0, byLength, refs: [] };
    line.count += 1;
    line.refs.push(ref);
    if (byLength) line.length = line.length === null || length === null || length === undefined ? null : line.length + length;
    lines.set(key, line);
  };
  for (const node of document.nodes ?? []) {
    if (!shown.has(node.local_id)) continue;
    const template = node.template_version_id ? library.scene.templates[node.template_version_id] : undefined;
    const name = (node.template_version_id ? library.versions[node.template_version_id]?.name : undefined) ?? node.role.replace(/_/g, " ");
    const volume = effectiveParameters(node, template).find((row) => row.key === "nominal_volume")?.effective;
    const volumeUnit = liquidUnitFor("meter ** 3", liquid);
    const capacity = typeof volume === "number" ? formatQuantity(volume, "meter ** 3", volumeUnit ?? undefined) : null;
    const item = [name, capacity].filter(Boolean).join(", ");
    add(`n:${item}`, node.local_id, item, false);
  }
  for (const edge of document.edges ?? []) {
    if (!shown.has(edge.local_id)) continue;
    const template = edge.template_version_id ? library.scene.templates[edge.template_version_id] : undefined;
    const name = (edge.template_version_id ? library.versions[edge.template_version_id]?.name : undefined) ?? (edge.type ?? "line").replace(/_/g, " ");
    const value = (key: string) => effectiveParameters(edge, template).find((row) => row.key === key)?.effective;
    const size = (key: string) => (typeof value(key) === "number" ? formatQuantity(value(key) as number, "meter", lengthUnit ?? MILLIMETRE) : null);
    const bore = size("width");
    const outer = size("outer_diameter");
    const material = typeof value("material") === "string" ? String(value("material")).replace(/_/g, " ") : null;
    const item = [name, [bore, outer].filter(Boolean).join(" × "), material].filter(Boolean).join(", ");
    add(`e:${item}`, edge.local_id, item, true, edge.hydraulic?.length ?? null);
  }
  return { side, lines: [...lines.values()].sort((a, b) => a.item.localeCompare(b.item)), tubeUnit };
}

/** A line's quantity, and the unit it is in. */
export function bomQuantity(line: BomLine, tubeUnit: DisplayUnit): { quantity: string; unit: string } {
  if (!line.byLength) return { quantity: String(line.count), unit: "ea" };
  return { quantity: line.length === null ? "?" : formatNumber(fromCanonical(line.length, tubeUnit)), unit: tubeUnit.label };
}

/** How many pieces of tube a line is cut into, or null for a part. */
export function bomCuts(line: BomLine): string | null {
  return line.byLength ? (line.count === 1 ? "1 cut" : `${line.count} cuts`) : null;
}

/** Whether the design has a set boundary, so either side has a bill of its own. */
export function hasBillOfMaterials(document: DesignDocument, library: WorkspaceLibrary): boolean {
  const sides = partition(document, library.scene.templates);
  return sides.inside.size > 0 || sides.outside.size > 0;
}

/** The two bills of materials an export can carry, named as the PDF names their views. */
export type BomId = "sus-bom" | "instrument-bom";

export const BOM_EXPORTS: readonly { id: BomId; side: BomSide; label: string; note: string; fill: string }[] = [
  {
    id: "sus-bom",
    side: "inside",
    label: "SUS bill of materials",
    note: "The single-use set. The instrument's parts are left out.",
    fill: THEME.washInside,
  },
  {
    id: "instrument-bom",
    side: "outside",
    label: "Instrument bill of materials",
    note: "Outside the set. Includes the instrument's clamp-on parts.",
    fill: THEME.washOutside,
  },
];

/** The chosen bills of materials as tables for a still, in `BOM_EXPORTS` order. */
export function bomTables(
  document: DesignDocument,
  library: WorkspaceLibrary,
  liquid: LiquidUnits | null,
  chosen: ReadonlySet<string>,
): StillTable[] {
  return BOM_EXPORTS.filter((entry) => chosen.has(entry.id)).map((entry) => {
    const bom = billOfMaterials(document, library, entry.side, liquid);
    return {
      legend: { label: entry.label, swatch: { fill: entry.fill } },
      note: entry.note,
      columns: ["Item", "Qty", "Unit"],
      rows: bom.lines.map((line) => ({ item: line.item, detail: bomCuts(line), ...bomQuantity(line, bom.tubeUnit) })),
      empty: "Nothing on this side.",
    };
  });
}
